import type { ReactNode } from "react";
import { Home, MessageCircleHeart, Users, UserRound } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Mobile: bottom-tab 네비.  PC(lg+): 좌측 사이드 네비.
 * 내용 컨텐츠는 children.
 */
export function AppShell({
  children,
  active = "home",
  topRight,
  onNavigate,
}: {
  children: ReactNode;
  active?: "home" | "matches" | "chat" | "profile";
  topRight?: ReactNode;
  onNavigate?: (next: "home" | "matches" | "chat" | "profile") => void;
}) {
  const items: {
    k: "home" | "matches" | "chat" | "profile";
    label: string;
    icon: ReactNode;
  }[] = [
    { k: "home", label: "Home", icon: <Home className="h-5 w-5" /> },
    { k: "matches", label: "Matches", icon: <Users className="h-5 w-5" /> },
    { k: "chat", label: "Chat", icon: <MessageCircleHeart className="h-5 w-5" /> },
    { k: "profile", label: "Profile", icon: <UserRound className="h-5 w-5" /> },
  ];

  return (
    <div className="min-h-screen w-full bg-bg text-fg">
      {/* Top bar (모바일/PC 공용) */}
      <header className="sticky top-0 z-40 kj-glass border-b border-border/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-7 w-7 rounded-xl bg-gradient-to-br from-primary-500 via-sakura-500 to-trust-500 shadow-glass-md"
            />
            <span className="text-base font-semibold tracking-tight">
              SkyToLove
            </span>
          </div>
          <div className="flex items-center gap-2">{topRight}</div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-7xl gap-6 px-2 pb-24 pt-4 lg:px-8 lg:pb-8">
        {/* Sidebar (lg+) */}
        <aside className="hidden lg:flex w-56 shrink-0 flex-col gap-1 py-2">
          {items.map((i) => (
            <button
              key={i.k}
              type="button"
              onClick={() => onNavigate?.(i.k)}
              className={cn(
                "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                active === i.k
                  ? "bg-primary-500/15 text-primary-500"
                  : "text-fg-muted hover:bg-white/5 hover:text-fg",
              )}
              aria-current={active === i.k ? "page" : undefined}
            >
              {i.icon}
              {i.label}
            </button>
          ))}
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">{children}</main>
      </div>

      {/* Bottom tab (mobile only) */}
      <nav
        aria-label="primary"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 kj-glass lg:hidden"
      >
        <ul className="mx-auto grid max-w-md grid-cols-4 px-2 py-1.5">
          {items.map((i) => (
            <li key={i.k}>
              <button
                type="button"
                onClick={() => onNavigate?.(i.k)}
                className={cn(
                  "flex w-full flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[0.68rem]",
                  active === i.k
                    ? "text-primary-500"
                    : "text-fg-muted hover:text-fg",
                )}
                aria-current={active === i.k ? "page" : undefined}
              >
                {i.icon}
                {i.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
